import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroTextButton } from '../../../shared/components';
import { useApi } from '../../../shared/context';
import { QueryKeys } from '../../../shared/hooks';
import { ActiveDataRoomUser, DataRoomUser } from '../../../shared/models';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getResponseData } from '../../../shared/utils/axios';

interface LenderGrantAccessDialogProps {
	dataRoomGuid: string | undefined;
	user: DataRoomUser | null;
	close: () => void;
}

export const LenderGrantAccessDialog: React.FC<LenderGrantAccessDialogProps> = ({ dataRoomGuid, user, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onAcceptConfirmation = () => {
		if (user !== null) acceptLender(user);
	};

	const { mutate: acceptLender } = useMutation<ActiveDataRoomUser, unknown, DataRoomUser>(
		userToAccept =>
			getResponseData(
				api.post<ActiveDataRoomUser>(CerebroApiEndpoints.DataRoomUsers.Grant(dataRoomGuid, userToAccept.guid))
			),
		{
			onMutate: () => {
				close();
			},
			onSuccess: () => {
				Promise.all([
					client.invalidateQueries(QueryKeys.DataRoomUsers.Root(dataRoomGuid)),
					client.invalidateQueries(QueryKeys.Milestones.Root(dataRoomGuid)),
					client.invalidateQueries(QueryKeys.DataRoomUsers.TeamMembersByRoom(dataRoomGuid)),
				]).finally(() => {
					snackbar.enqueueSnackbar('Lender Accepted.', { variant: 'success' });
					close();
				});
			},
			onError: () => {
				client.invalidateQueries(QueryKeys.DataRoomUsers.Root(dataRoomGuid));
				snackbar.enqueueSnackbar('There was a problem accepting the lender.', { variant: 'error' });
			},
		}
	);

	return (
		<Dialog open={user !== null} onClose={close}>
			<DialogTitle>Confirm Accept Lender</DialogTitle>
			<DialogContent>
				<DialogContentText>
					Are you sure you want to accept {user?.firstName} {user?.lastName} at {user?.bankName}?
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onAcceptConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
